import { type MotorContractCoverageChangeRequest } from '@/typesTenant/MotorContractChange'
import type { VehicleSearchResult } from '@/typesTenant/VehicleSelectionApi'
import type {
  ChangeVehicleApplication,
  ChangeVehicleConclusion,
  CompleteChangeVehicleOffer,
  MotorConclusionResponse
} from '@/typesTenant/NewVehicleDetailApi'
import { createMotorHeaders } from '@/stores/motorService'

export async function submitMotorContractChange(
  partnerProxyId: string,
  motorContractCoverageChangeRequest: MotorContractCoverageChangeRequest
): Promise<boolean> {
  const headers = await createMotorHeaders()

  try {
    const response = await fetch(
      `${globalThis.ENV_CONFIG?.SERVICE_HOSTS.MOTOR}/self-services/change-motor-contract/${partnerProxyId}`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(motorContractCoverageChangeRequest)
      }
    )

    return response.ok
  } catch (_error) {
    return false
  }
}

export async function computePriceForContractChange(
  motorContractCoverageChangeRequest: MotorContractCoverageChangeRequest
): Promise<number> {
  const headers = await createMotorHeaders()

  const sanitizedRequest: MotorContractCoverageChangeRequest = {
    ...motorContractCoverageChangeRequest
  }

  if (sanitizedRequest.mileage == null) {
    delete sanitizedRequest.mileage
  }

  if (
    sanitizedRequest.insuranceCover &&
    sanitizedRequest.insuranceCover.newExtras &&
    sanitizedRequest.insuranceCover.newExtras.liabilityProduct
  ) {
    delete sanitizedRequest.insuranceCover.newExtras.liabilityProduct.productTarif
  }

  if (
    sanitizedRequest.insuranceCover &&
    sanitizedRequest.insuranceCover.oldExtras &&
    sanitizedRequest.insuranceCover.oldExtras.liabilityProduct
  ) {
    delete sanitizedRequest.insuranceCover.oldExtras.liabilityProduct.productTarif
  }

  const response = await fetch(
    `${globalThis.ENV_CONFIG?.SERVICE_HOSTS.MOTOR}/computePriceForContractChange`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify(motorContractCoverageChangeRequest)
    }
  )

  if (!response.ok) {
    throw new Error('Failed to compute price for contract change')
  }

  return await response.json()
}

export async function searchVehicle(
  manufacturerId: string,
  typeKey: string
): Promise<VehicleSearchResult> {
  const headers = await createMotorHeaders()

  const queryParams = new URLSearchParams({
    manufacturerId,
    typeKey
  }).toString()

  const response = await fetch(
    `${globalThis.ENV_CONFIG?.SERVICE_HOSTS.MOTOR}/search?${queryParams}`,
    {
      method: 'GET',
      headers
    }
  )

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Unauthorized')
    } else if (response.status === 404) {
      throw new Error('NotFound')
    } else if (response.status === 409) {
      throw new Error('Conflict')
    }
    throw new Error(`Error searching vehicle: ${response.statusText}`)
  }

  return await response.json()
}

export async function fetchVehicleChangeQuotes(
  application: ChangeVehicleApplication
): Promise<CompleteChangeVehicleOffer> {
  const headers = await createMotorHeaders()

  const response = await fetch(
    `${globalThis.ENV_CONFIG?.SERVICE_HOSTS.MOTOR}/change/contracts/vehicle-change-quotes`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify(application)
    }
  )

  if (!response.ok) {
    throw new Error(`Error fetching vehicle change quotes: ${response.statusText}`)
  }

  return await response.json()
}

export async function submitMotorConclusion(
  conclusion: ChangeVehicleConclusion
): Promise<MotorConclusionResponse> {
  const headers = await createMotorHeaders()

  const response = await fetch(
    `${globalThis.ENV_CONFIG?.SERVICE_HOSTS.MOTOR}/motor/conclusions`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify(conclusion)
    }
  )

  if (!response.ok) {
    throw new Error(`Error submitting motor conclusion: ${response.statusText}`)
  }

  return await response.json()
}
